import { supabase } from "@/integrations/supabase/client";

type Severity = "error" | "warning" | "info";

interface LogPayload {
  source: string;
  message: string;
  severity?: Severity;
  stack?: string;
  context?: Record<string, unknown>;
}

const recentErrors = new Map<string, number>();
const DEDUPE_MS = 60_000;

function shouldSend(key: string): boolean {
  const now = Date.now();
  const last = recentErrors.get(key);
  if (last && now - last < DEDUPE_MS) return false;
  recentErrors.set(key, now);
  if (recentErrors.size > 200) {
    for (const [k, t] of recentErrors) if (now - t > DEDUPE_MS) recentErrors.delete(k);
  }
  return true;
}

export async function logError(payload: LogPayload): Promise<void> {
  const severity = payload.severity ?? "error";
  // Always console for dev visibility
  const consoleFn = severity === "error" ? console.error : severity === "warning" ? console.warn : console.info;
  consoleFn(`[${payload.source}]`, payload.message, payload.context ?? "", payload.stack ?? "");

  const dedupeKey = `${payload.source}:${payload.message}`;
  if (!shouldSend(dedupeKey)) return;

  try {
    const { data: { user } } = await supabase.auth.getUser();
    await (supabase.from("error_logs") as any).insert({
      source: payload.source,
      severity,
      message: payload.message.slice(0, 2000),
      stack: payload.stack?.slice(0, 8000),
      url: typeof window !== "undefined" ? window.location.href : null,
      user_id: user?.id ?? null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      context: payload.context ?? null,
    });
  } catch {
    // Swallow; never let logging break the app
  }
}

export function installGlobalErrorHandlers(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (e) => {
    logError({
      source: "window.onerror",
      message: e.message || "Unknown error",
      stack: e.error?.stack,
      context: { filename: e.filename, lineno: e.lineno, colno: e.colno },
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = (e.reason ?? {}) as { message?: string; stack?: string };
    logError({
      source: "unhandledrejection",
      message: reason.message || String(e.reason),
      stack: reason.stack,
    });
  });
}
